/**
 * Confirms irreversible purge of an employee and all onboarding data.
 */
import React, { useState } from 'react';
import { deleteEmployeeCompletely } from '../../../../services/employeeDeleteApi';
import './EmployeePurgeModal.css';

export interface EmployeePurgeModalProps {
  open: boolean;
  employeeName: string;
  employeeEmail: string;
  onClose: () => void;
  onPurged: () => void;
}

const EmployeePurgeModal: React.FC<EmployeePurgeModalProps> = ({
  open,
  employeeName,
  employeeEmail,
  onClose,
  onPurged,
}) => {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [downloadedAck, setDownloadedAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const emailTrim = employeeEmail.trim();
  const confirmOk = confirmEmail.trim().toLowerCase() === emailTrim.toLowerCase();
  const canSubmit = downloadedAck && confirmOk && !submitting;

  const handleClose = () => {
    if (submitting) return;
    setConfirmEmail('');
    setDownloadedAck(false);
    setError('');
    onClose();
  };

  const handlePurge = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await deleteEmployeeCompletely(emailTrim, confirmEmail.trim());
      setConfirmEmail('');
      setDownloadedAck(false);
      onPurged();
    } catch (e) {
      setError(e instanceof Error ? e.message : '削除に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="employee-purge-modal" role="dialog" aria-modal="true" aria-labelledby="employee-purge-title">
      <div className="employee-purge-modal__backdrop" onClick={handleClose} aria-hidden />
      <div className="employee-purge-modal__box">
        <button type="button" className="employee-purge-modal__close" aria-label="閉じる" onClick={handleClose}>
          ×
        </button>
        <h2 id="employee-purge-title" className="employee-purge-modal__title">
          従業員データを完全に削除しますか？
        </h2>
        <div className="employee-purge-modal__warning">
          <p>
            <strong>{employeeName || emailTrim}</strong>（{emailTrim}）に関するアカウント、提出書類、アップロードファイル、
            招待・雇用履歴などを<strong>すべて削除</strong>します。この操作は取り消せません。
          </p>
          <ul>
            <li>先に「ZIPをダウンロード」で書類一式を保存してください。</li>
            <li>削除後、同じメールアドレスで新規登録できます。</li>
            <li>在職中の従業員は削除できません（退職記録が必要です）。</li>
          </ul>
        </div>
        <label className="employee-purge-modal__checkbox-row">
          <input
            type="checkbox"
            checked={downloadedAck}
            onChange={(e) => setDownloadedAck(e.target.checked)}
            disabled={submitting}
          />
          <span>必要な書類をダウンロード済みであることを確認しました。</span>
        </label>
        <label className="employee-purge-modal__label" htmlFor="employee-purge-confirm-email">
          確認のため、メールアドレスを入力してください
        </label>
        <input
          id="employee-purge-confirm-email"
          type="email"
          className="employee-purge-modal__input"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder={emailTrim}
          autoComplete="off"
          disabled={submitting}
        />
        {error ? <p className="employee-purge-modal__error">{error}</p> : null}
        <div className="employee-purge-modal__footer">
          <button type="button" className="employee-purge-modal__cancel-btn" onClick={handleClose} disabled={submitting}>
            キャンセル
          </button>
          <button type="button" className="employee-purge-modal__purge-btn" onClick={() => void handlePurge()} disabled={!canSubmit}>
            {submitting ? '削除中…' : '完全に削除する'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeePurgeModal;
